import { DetailRepository, DetailService } from '@aipb/gulliver/catalog/data-access';
import { DetailItem } from '@aipb/gulliver/catalog/model';
import { CatalogRouting } from '@aipb/gulliver/catalog/routing';
import { isDropbox, parseDetailCompositeKey } from '@aipb/gulliver/catalog/util';
import { DropboxRepository, DropboxService } from '@aipb/gulliver/dropbox/data-access';
import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { RouterRepository } from '@ngneat/elf-ng-router-store';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, forkJoin, Observable, of } from 'rxjs';
import { filter, map, mergeMap, tap } from 'rxjs/operators';
import { CompareDesktopComponent, CompareMobileComponent } from '../../components';

@UntilDestroy()
@Component({
  selector: 'cat-compare-page',
  templateUrl: './compare-page.component.html',
  styleUrls: ['./compare-page.component.scss'],
  standalone: true,
  imports: [CommonModule, CompareDesktopComponent, CompareMobileComponent]
})
export class ComparePageComponent implements OnInit {
  first$: Observable<DetailItem> = of(null);
  second$: Observable<DetailItem> = of(null);
  private routerRepository = inject(RouterRepository);
  private detailRepository = inject(DetailRepository);
  private detailService = inject(DetailService);
  private catalogRouting = inject(CatalogRouting);
  private dropbox = inject(DropboxService);
  private dropboxRepository = inject(DropboxRepository);

  ngOnInit(): void {
    this.routerRepository
      .selectParams()
      .pipe(
        mergeMap((params) => {
          const id1 = params['id1'];
          const id2 = params['id2'];

          this.first$ = this.detailRepository.selectDetail(id1);
          this.second$ = this.detailRepository.selectDetail(id2);

          return combineLatest([this.first$, this.second$]).pipe(
            mergeMap(() =>
              forkJoin([
                this.fetchDetail(id1).pipe(
                  filter((detail) => isDropbox(detail.document.GulliverRecType)),
                  map((detail) => [parseDetailCompositeKey(detail.id)[1], detail.document.PathDisplay]),
                  filter(([ID]) => !this.dropboxRepository.getImage(ID)),
                  mergeMap(([ID, path]) => {
                    return this.dropbox.imageDownloadBatch([{ id: ID, path }]);
                  })
                ),
                this.fetchDetail(id2).pipe(
                  filter((detail) => isDropbox(detail.document.GulliverRecType)),
                  map((detail) => [parseDetailCompositeKey(detail.id)[1], detail.document.PathDisplay]),
                  filter(([ID]) => !this.dropboxRepository.getImage(ID)),
                  mergeMap(([ID, path]) => {
                    return this.dropbox.imageDownloadBatch([{ id: ID, path }]);
                  })
                )
              ])
            ),
            untilDestroyed(this)
          );
        }),
        untilDestroyed(this)
      )
      .subscribe();
  }

  fetchDetail(ID: string): Observable<DetailItem> {
    if (this.detailRepository.hasDetail(ID)) {
      return this.detailRepository.selectDetail(ID).pipe(untilDestroyed(this));
    }

    const s = parseDetailCompositeKey(ID);

    return this.detailService.fetchDetail(s[1], s[0], '*:*').pipe(
      tap((detail) => {
        this.detailService.addTab({
          id: detail.id,
          type: detail.document.GulliverRecType,
          title: detail.document.GulliverTabName,
          query: detail.query,
          image: detail.document.GulliverRecType !== 'Dropbox' ? detail.imageThumb || detail.imageLarge : null
        });
      }),
      mergeMap(() => this.detailRepository.selectDetail(ID)),
      untilDestroyed(this)
    );
  }

  onViewDetail(detail: DetailItem): void {
    this.catalogRouting.detailSingle(detail.id);
  }
}
